// Respaldo bilingue de los avisos del dominio de inmuebles: alta de propiedad
// (socio de negocio y broker), catalogo de co-living/co-working, detalle de
// propiedad y flujo de reserva con acompanantes.
//
// POR QUE EXISTE: casi todos son el `||` que cubre una clave del esquema remoto
// (/api/lang/sch) que hoy no llega, y estaban escritos solo en espanol, asi que
// quien tenia la interfaz en ingles los leia en otro idioma que el resto de la
// pantalla. Unos pocos ni siquiera tenian clave y se pintaban siempre en espanol.
//
// COMO SE RETIRA: el esquema remoto SIEMPRE gana donde hay clave; el dia que
// backend publique las suyas estos respaldos dejan de usarse solos.
//
// Las redacciones en espanol son las que ya estaban en el codigo, sin retocar:
// esto localiza los avisos, no reescribe la copia. Por eso conviven
// `selectStayDates` y `selectStayDatesRequest`, que dicen lo mismo con distintas
// palabras en dos pantallas.

import { normalizeInterfaceLanguage } from "../interfaceLanguage";

const propertyMessages = {
  es: {
    // Alta de propiedad
    loadBusinessInfoFailed: "Error al cargar información comercial",
    completeRequiredFields: "Completa los campos obligatorios.",
    titleRequired: "El título es obligatorio.",
    detailsSaved: "Detalles guardados correctamente.",
    detailsSaveFailed: "No se pudieron guardar los detalles.",
    loadPetsCatalogFailed: "Error al cargar el catálogo de mascotas",
    loadCostCategoriesFailed: "Error al cargar las categorías de costo",
    catalogsLoading: "Espera mientras se cargan los catálogos.",
    minimumStayRequired: "La estancia mínima es obligatoria.",
    selectPetType: "Selecciona al menos un tipo de mascota.",
    completeOtherPetDetails: "Completa el detalle de la mascota en \"Otro\".",
    maxPetsRequired: "El número máximo de mascotas es obligatorio.",
    leaseConditionsSaved: "Condiciones de arrendamiento guardadas.",
    leaseConditionsFailed: "Error al insertar las condiciones de arrendamiento.",
    emptyAmenity: "La amenidad no puede quedar vacía.",
    amenityAlreadyExists: "Esa amenidad ya existe.",
    selectPropertyType: "Selecciona un tipo de propiedad",
    savePropertyOrPhotosFailed: "Hubo un error al guardar la propiedad o sus fotos",
    propertyPublished: "Propiedad publicada correctamente.",
    postalCodeNotFound: "No se encontró información del código postal",
    geocodeFailed: "No pudimos convertir la dirección en coordenadas.",
    addressSaved: "Dirección guardada correctamente",
    addressSaveFailed: "No se pudo guardar la dirección",
    maxPhotos: "Solo puedes agregar hasta 30 fotos",
    taxDocumentSaved: "Documento fiscal guardado correctamente.",
    infoSaved: "Información guardada correctamente",
    documentsUploadedSuccessfully: "Documentos subidos correctamente",
    smsOrEmailNotSent: "No se envió el SMS ni el correo",
    ownerInfoSent: "Información del propietario enviada correctamente",
    acknowledgeRequirements: "Debes aceptar los requisitos.",
    missingFolio: "Falta el folio.",
    infoUpdated: "Información actualizada correctamente",
    updateInfoFailed: "Error al actualizar información",
    fileNotImage: "El archivo no es una imagen.",
    imageTooLarge: "La imagen supera 5 MB.",
    maxImages: "Máximo 10 imágenes.",
    minPhotos: "Sube al menos 5 fotos.",
    missingPropertyId: "Falta el ID de la propiedad.",
    uploadImagesFailed: "No se pudieron subir/enlazar las imágenes.",
    imagesUploaded: "Imágenes cargadas.",
    invalidPrice: "Precio inválido.",
    priceSaved: "Precio guardado.",
    savePriceFailed: "No se pudo guardar el precio.",
    arrivalGuideUpdated: "Guia de llegada actualizada con éxito",
    saveChangesFailed: "Error al intentar guardar cambios",
    propertyUpdated: "Propiedad actualizada con éxito",
    emptyRule: "La regla no puede quedar vacia.",
    writeRuleFirst: "Escribe una regla antes de agregarla.",
    ruleAlreadyExists: "Esa regla ya existe.",
    writeAmenityFirst: "Escribe una amenidad antes de agregarla.",
    amenitiesSaved: "Amenidades guardadas.",
    photosLinked: "Fotos asociadas a la propiedad.",
    documentsUploaded: "Todos los documentos fueron subidos y validados.",
    oneCostPerCategory: "Solo se permite un costo por categoria.",
    selectAmenity: "Selecciona al menos una amenidad.",
    selectOption: "Selecciona una opción para continuar",
    postalCodeFailed: "No se pudo obtener la informacion del codigo postal.",
    subtypesFailed: "No se pudieron obtener subtipos",
    selectSubtype: "Selecciona un subtipo para continuar",
    noUserSession: "No hay sesión de usuario",
    propertyCreated: "Propiedad creada",
    createPropertyFailed: "No se pudo crear la propiedad",
    missingOwnerId: "Falta IdPropietario.",
    missingPropertyDataId: "Falta IdDatosInmueble.",
    uploadTaxCertificate: "Carga la constancia de situación fiscal.",
    unsupportedPdfFormat: "Formato no soportado. Solo se acepta PDF.",
    fileTooLarge: "El archivo es demasiado grande.",
    maxFileSizeSuffix: (mb) => ` Máx ${mb} MB.`,
    saveTaxCertificateFailed:
      "No se pudo guardar la constancia. Intenta de nuevo más tarde.",

    // Verificación de socio de negocio / identidad
    requestNotCreated: "No se ha creado la solicitud",
    onlyPdfOrImages: "Solo se permiten archivos PDF o imágenes.",

    // Catálogo y detalle
    loadPropertiesFailed: "No se pudieron cargar las propiedades",
    loadPropertyDetailsFailed: "No se pudieron cargar los detalles de la propiedad",
    loadListingsFailed: "No se pudieron cargar los inmuebles.",
    propertySaved: "Propiedad guardada",
    propertyUnsaved: "Propiedad eliminada de guardados",
    updateFavoritesFailed: "Error al actualizar favoritos",
    loadImagesFailed: "Error cargando imágenes",
    loadImageFailed: "Error al cargar la imagen",

    // Reserva
    selectStayDates: "Selecciona las fechas de llegada y salida",
    selectStayDatesRequest: "Por favor selecciona fechas de llegada y salida",
    reservationRequested: "¡Reserva solicitada! Te contactaremos a la brevedad.",
    mustBeVerifiedUser: "Debes ser usuario verificado",
    createReservationFailed: "Error al generar la reserva",
    uploadDocumentFirst: "Por favor, sube un documento antes de continuar.",
    selectRelationship: "Debes seleccionar una relación",
    processDocumentFailed: "Error al procesar el documento. Intenta nuevamente.",
    completeAuthCode: "Por favor completa todos los dígitos del código",
    validatedCorrectly: "Validado de forma correcta",
    fillAllFields: "Favor de llenar todos los campos",
    proofOfLifeRequired: "Debes realizar la prueba de vida para continuar",
    fillRequiredFields: "Debes llenar todos los campos obligatorios",
    uploadInfoFailed: "Error al subir información",
  },
  en: {
    // Alta de propiedad
    loadBusinessInfoFailed: "Could not load the business information",
    completeRequiredFields: "Please complete the required fields.",
    titleRequired: "The title is required.",
    detailsSaved: "Details saved successfully.",
    detailsSaveFailed: "Details could not be saved.",
    loadPetsCatalogFailed: "Error loading pets catalog",
    loadCostCategoriesFailed: "Error loading cost categories",
    catalogsLoading: "Please wait while the catalogs are loading.",
    minimumStayRequired: "Minimum stay is required.",
    selectPetType: "Select at least one pet type.",
    completeOtherPetDetails: "Complete the \"Other\" pet details.",
    maxPetsRequired: "Maximum number of pets is required.",
    leaseConditionsSaved: "Lease conditions saved.",
    leaseConditionsFailed: "Error inserting lease conditions.",
    emptyAmenity: "The amenity cannot be empty.",
    amenityAlreadyExists: "That amenity already exists.",
    selectPropertyType: "Select a property type",
    savePropertyOrPhotosFailed: "There was an error saving the property or its photos",
    propertyPublished: "Property published successfully.",
    postalCodeNotFound: "No postal code information was found",
    geocodeFailed: "We could not convert the address into coordinates.",
    addressSaved: "Address saved successfully",
    addressSaveFailed: "Cannot save address",
    maxPhotos: "You can only add up to 30 photos",
    taxDocumentSaved: "Tax document successfully saved.",
    infoSaved: "Information saved successfully",
    documentsUploadedSuccessfully: "Documents uploaded successfully",
    smsOrEmailNotSent: "SMS or email not sent",
    ownerInfoSent: "Owner information sent successfully",
    acknowledgeRequirements: "You must acknowledge the requirements.",
    missingFolio: "Folio is missing.",
    infoUpdated: "Information updated successfully",
    updateInfoFailed: "Could not update the information",
    fileNotImage: "The file is not an image.",
    imageTooLarge: "The image is larger than 5 MB.",
    maxImages: "Up to 10 images.",
    minPhotos: "Upload at least 5 photos.",
    missingPropertyId: "The property ID is missing.",
    uploadImagesFailed: "The images could not be uploaded or linked.",
    imagesUploaded: "Images uploaded.",
    invalidPrice: "Invalid price.",
    priceSaved: "Price saved.",
    savePriceFailed: "Could not save the price.",
    arrivalGuideUpdated: "Arrival guide updated successfully",
    saveChangesFailed: "Could not save the changes",
    propertyUpdated: "Property updated successfully",
    emptyRule: "The rule cannot be empty.",
    writeRuleFirst: "Type a rule before adding it.",
    ruleAlreadyExists: "That rule already exists.",
    writeAmenityFirst: "Type an amenity before adding it.",
    amenitiesSaved: "Amenities saved.",
    photosLinked: "Photos linked to the property.",
    documentsUploaded: "All documents were uploaded and validated.",
    oneCostPerCategory: "Only one cost per category is allowed.",
    selectAmenity: "Select at least one amenity.",
    selectOption: "Select an option to continue",
    postalCodeFailed: "Could not get the postal code information.",
    subtypesFailed: "Could not get the subtypes",
    selectSubtype: "Select a subtype to continue",
    noUserSession: "There is no user session",
    propertyCreated: "Property created",
    createPropertyFailed: "Could not create the property",
    missingOwnerId: "The owner ID is missing.",
    missingPropertyDataId: "The property data ID is missing.",
    uploadTaxCertificate: "Upload your tax status certificate.",
    unsupportedPdfFormat: "Unsupported format. Only PDF is accepted.",
    fileTooLarge: "The file is too large.",
    maxFileSizeSuffix: (mb) => ` Max ${mb} MB.`,
    saveTaxCertificateFailed:
      "Could not save the certificate. Please try again later.",

    // Verificación de socio de negocio / identidad
    requestNotCreated: "The request has not been created",
    onlyPdfOrImages: "Only PDF files or images are allowed.",

    // Catálogo y detalle
    loadPropertiesFailed: "Could not load the properties",
    loadPropertyDetailsFailed: "Could not load the property details",
    loadListingsFailed: "Could not load the listings.",
    propertySaved: "Property saved",
    propertyUnsaved: "Property removed from saved",
    updateFavoritesFailed: "Could not update your favorites",
    loadImagesFailed: "Error loading images",
    loadImageFailed: "Could not load the image",

    // Reserva
    selectStayDates: "Select your arrival and departure dates",
    selectStayDatesRequest: "Please select arrival and departure dates",
    reservationRequested: "Reservation requested. We will contact you shortly.",
    mustBeVerifiedUser: "You must be a verified user",
    createReservationFailed: "Could not create the reservation",
    uploadDocumentFirst: "Please upload a document before continuing.",
    selectRelationship: "You must select a relationship",
    processDocumentFailed: "Could not process the document. Try again.",
    completeAuthCode: "Please complete every digit of the code",
    validatedCorrectly: "Validated successfully",
    fillAllFields: "Please fill in every field",
    proofOfLifeRequired: "You must complete the proof of life to continue",
    fillRequiredFields: "You must fill in every required field",
    uploadInfoFailed: "Could not upload the information",
  },
};

export const getPropertyMessages = (language) =>
  propertyMessages[normalizeInterfaceLanguage(language)];
